import React, { useState } from 'react';
import { digitalEmployees } from '../data';
import { EmployeeCategory, Industry } from '../types';
import TeslaEmployeeCard from '../components/TeslaEmployeeCard';
import TeslaRobot from '../components/TeslaRobot';

const BrowsePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'categories' | 'industries'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<EmployeeCategory | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);
  
  // Get all available categories and industries
  const categories: EmployeeCategory[] = ['HR', 'PMO', 'Developer', 'Personal Assistant', 'RFP Developer'];
  const industries: Industry[] = ['Real Estate', 'Education', 'Transportation', 'Hospitality', 'Finance'];
  
  // Filter employees based on selected filters
  const filteredEmployees = digitalEmployees.filter(employee => {
    if (activeTab === 'categories' && selectedCategory) {
      return employee.category === selectedCategory;
    } else if (activeTab === 'industries' && selectedIndustry) {
      return employee.industries.includes(selectedIndustry);
    }
    return true;
  });
  
  // Get count of employees in each category/industry
  const getCategoryCount = (category: EmployeeCategory): number => {
    return digitalEmployees.filter(employee => employee.category === category).length;
  };
  
  const getIndustryCount = (industry: Industry): number => {
    return digitalEmployees.filter(employee => employee.industries.includes(industry)).length;
  };
  
  // Get colors for categories and industries
  const getCategoryColor = (category: EmployeeCategory): string => {
    const colorMap: Record<EmployeeCategory, string> = {
      'HR': '#4a6bff',
      'PMO': '#6e3adc',
      'Developer': '#00f7ff',
      'Personal Assistant': '#ff4757',
      'RFP Developer': '#ffbc42'
    };
    
    return colorMap[category];
  };
  
  const getIndustryColor = (industry: Industry): string => {
    const colorMap: Record<Industry, string> = {
      'Real Estate': '#4a6bff',
      'Education': '#6e3adc',
      'Transportation': '#00f7ff',
      'Hospitality': '#ff4757',
      'Finance': '#ffbc42'
    };
    
    return colorMap[industry];
  };
  
  // Get icons for industries
  const getIndustryIcon = (industry: Industry): string => {
    const iconMap: Record<Industry, string> = {
      'Real Estate': '🏢',
      'Education': '🎓',
      'Transportation': '🚗',
      'Hospitality': '🏨',
      'Finance': '💰'
    };
    
    return iconMap[industry];
  };
  
  return (
    <div className="browse-page">
      <div className="browse-header">
        <div className="browse-title-container">
          <h1 className="browse-title">
            Browse <span className="text-gradient">AI Employees</span>
          </h1>
          <p className="browse-subtitle">
            Explore our collection of specialized digital employees by category or industry
          </p>
        </div>
        <div className="browse-robot">
          <TeslaRobot width={100} height={100} />
        </div>
      </div>
      
      <div className="browse-tabs">
        <button 
          className={`browse-tab ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('categories');
            setSelectedIndustry(null);
          }}
        >
          Categories
        </button>
        <button 
          className={`browse-tab ${activeTab === 'industries' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('industries');
            setSelectedCategory(null);
          }}
        >
          Industries
        </button>
      </div>
      
      <div className="browse-filters">
        {activeTab === 'categories' ? (
          <div className="category-filters">
            <button 
              className={`filter-button ${selectedCategory === null ? 'active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              All Categories
            </button>
            {categories.map(category => (
              <button 
                key={category}
                className={`filter-button ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
                style={{ 
                  '--filter-color': getCategoryColor(category)
                } as React.CSSProperties}
              >
                {category} <span className="filter-count">({getCategoryCount(category)})</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="industry-filters">
            <button 
              className={`filter-button ${selectedIndustry === null ? 'active' : ''}`}
              onClick={() => setSelectedIndustry(null)}
            >
              All Industries
            </button>
            {industries.map(industry => (
              <button 
                key={industry}
                className={`filter-button ${selectedIndustry === industry ? 'active' : ''}`}
                onClick={() => setSelectedIndustry(industry)}
                style={{ 
                  '--filter-color': getIndustryColor(industry)
                } as React.CSSProperties}
              >
                <span className="industry-icon-small">{getIndustryIcon(industry)}</span>
                {industry} <span className="filter-count">({getIndustryCount(industry)})</span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      <div className="browse-results">
        <div className="results-header">
          <h2>
            {activeTab === 'categories' ? (
              selectedCategory ? (
                <>
                  <span style={{ color: getCategoryColor(selectedCategory) }}>{selectedCategory}</span> Employees
                </>
              ) : 'All Categories'
            ) : (
              selectedIndustry ? (
                <>
                  <span className="industry-icon-small">{getIndustryIcon(selectedIndustry)}</span>
                  <span style={{ color: getIndustryColor(selectedIndustry) }}>{selectedIndustry}</span> Specialists
                </>
              ) : 'All Industries'
            )}
          </h2>
          <span className="results-count">{filteredEmployees.length} employees found</span>
        </div>
        
        {filteredEmployees.length > 0 ? (
          <div className="browse-employees-grid">
            {filteredEmployees.map(employee => (
              <TeslaEmployeeCard 
                key={employee.id} 
                employee={employee} 
                className="browse-employee-card"
              />
            ))}
          </div>
        ) : (
          <div className="no-browse-results">
            <div className="no-results-icon">
              <TeslaRobot width={80} height={80} />
            </div>
            <p>No employees found matching your criteria</p>
            <button 
              className="clear-filters-button"
              onClick={() => {
                if (activeTab === 'categories') {
                  setSelectedCategory(null);
                } else {
                  setSelectedIndustry(null);
                }
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowsePage;
